export type TenantActionStatus = 'pending' | 'in_progress' | 'completed' | 'dismissed' | 'expired';

export interface TenantActionRecord {
  actionKey: string;
  title: string;
  subtitle?: string;
  description?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  ctaKind?: string;
  priority?: number;
  required?: boolean;
  status: TenantActionStatus;
  attributes?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
  startedAt?: string | null;
  completedAt?: string | null;
  dismissedAt?: string | null;
}

interface TenantActionListResponse {
  items?: TenantActionRecord[];
}

export interface TenantApplicationRecord {
  id: string;
  applicantUserId: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'provisioned' | string;
  companyName: string;
  website?: string | null;
  employeeCountBand: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string | null;
  countryCode: string;
  companyType: string;
  intendedUse?: string | null;
  notes?: string | null;
  latestReviewReason?: string | null;
  latestReviewAt?: string | null;
  provisionedTenantId?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface TenantApplicationUpsertPayload {
  companyName: string;
  website?: string | null;
  employeeCountBand: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string | null;
  countryCode: string;
  companyType: string;
  intendedUse?: string | null;
  notes?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface OnboardingSessionRecord {
  id: string;
  status: 'started' | 'awaiting_auth' | 'attached' | 'expired' | string;
  intent: 'create_company' | 'accept_member_invite' | 'accept_partner_invite' | string;
  inviteToken?: string | null;
  applicationDraft?: Record<string, unknown> | null;
  attachedUserId?: string | null;
  expiresAt?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface OnboardingSessionPayload {
  status?: string;
  intent: string;
  inviteToken?: string | null;
  applicationDraft?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
  expiresAt?: string | null;
}

export class OnboardingError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'OnboardingError';
    this.status = status;
  }
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    ...init,
  });

  if (!response.ok) {
    let message = `Request failed: ${response.status}`;
    try {
      const body = await response.json() as { message?: string; detail?: string; title?: string };
      message = body.message || body.detail || body.title || message;
    } catch {
      const body = await response.text();
      if (body.trim()) {
        message = body;
      }
    }
    throw new OnboardingError(message, response.status);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export async function listTenantActions(tenantId: string): Promise<TenantActionRecord[]> {
  if (!tenantId) {
    return [];
  }
  const payload = await request<TenantActionListResponse>(`/api/v1/tenants/${tenantId}/actions`);
  return payload.items ?? [];
}

export async function getMyTenantApplication(): Promise<TenantApplicationRecord | null> {
  try {
    return await request<TenantApplicationRecord>('/api/v1/onboarding/me/application');
  } catch (error) {
    if (error instanceof OnboardingError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function saveMyTenantApplication(payload: TenantApplicationUpsertPayload): Promise<TenantApplicationRecord> {
  return request<TenantApplicationRecord>('/api/v1/onboarding/me/application', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function submitMyTenantApplication(): Promise<TenantApplicationRecord> {
  return request<TenantApplicationRecord>('/api/v1/onboarding/me/application/submit', {
    method: 'POST',
  });
}

export async function createOnboardingSession(payload: OnboardingSessionPayload): Promise<OnboardingSessionRecord> {
  return request<OnboardingSessionRecord>('/api/v1/onboarding/sessions', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getOnboardingSession(sessionId: string): Promise<OnboardingSessionRecord | null> {
  try {
    return await request<OnboardingSessionRecord>(`/api/v1/onboarding/sessions/${encodeURIComponent(sessionId)}`);
  } catch (error) {
    if (error instanceof OnboardingError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function updateOnboardingSession(sessionId: string, payload: Partial<OnboardingSessionPayload>): Promise<OnboardingSessionRecord> {
  return request<OnboardingSessionRecord>(`/api/v1/onboarding/sessions/${encodeURIComponent(sessionId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function attachOnboardingSession(sessionId: string): Promise<OnboardingSessionRecord> {
  return request<OnboardingSessionRecord>(`/api/v1/onboarding/sessions/${encodeURIComponent(sessionId)}/attach`, {
    method: 'POST',
  });
}
